import type { FileAttachment } from '@dashhudson/dashing-ui/use/file-attachments';

const ALLOWED_IMAGE_MIMES = ['image/png', 'image/jpeg'] as const;
const ALLOWED_VIDEO_MIMES = ['video/mp4'] as const;
const ALLOWED_PDF_MIMES = ['application/pdf'] as const;

export const ACCEPTED_TYPES = [
  ...ALLOWED_IMAGE_MIMES,
  ...ALLOWED_VIDEO_MIMES,
  ...ALLOWED_PDF_MIMES,
].join(',');

export const ATTACHMENT_MAX_BYTES = {
  image: 8 * 1024 * 1024,
  video: 25 * 1024 * 1024,
  pdf: 25 * 1024 * 1024,
} as const;

export type AttachmentKind = keyof typeof ATTACHMENT_MAX_BYTES;

export type AttachmentValidation = { ok: true } | { ok: false; summary: string; message: string };

export type AttachmentApiType = 'image' | 'video' | 'file';

const ATTACHMENT_API_TYPE: Record<AttachmentKind, AttachmentApiType> = {
  image: 'image',
  video: 'video',
  pdf: 'file',
};

export function getAttachmentKind(file: File): AttachmentKind | null {
  if ((ALLOWED_IMAGE_MIMES as readonly string[]).includes(file.type)) return 'image';
  if ((ALLOWED_VIDEO_MIMES as readonly string[]).includes(file.type)) return 'video';
  if ((ALLOWED_PDF_MIMES as readonly string[]).includes(file.type)) return 'pdf';
  return null;
}

export function getAttachmentApiType(file: File): AttachmentApiType | null {
  const kind = getAttachmentKind(file);
  return kind ? ATTACHMENT_API_TYPE[kind] : null;
}

export function getFileAttachmentSourceType(file: File): FileAttachment['sourceType'] {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
}

export async function fetchFileFromUrl(url: string, name?: string): Promise<File> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file (${response.status})`);
  }
  const blob = await response.blob();
  const resolvedName = name || url.split('/').pop() || 'attachment';
  return new File([blob], resolvedName, { type: blob.type });
}

export function toFileAttachment(file: File): FileAttachment {
  return {
    id: crypto.randomUUID(),
    file,
    url: URL.createObjectURL(file),
    name: file.name,
    type: file.type,
    sourceType: getFileAttachmentSourceType(file),
  };
}

export function validateAttachment(file: File): AttachmentValidation {
  const kind = getAttachmentKind(file);
  if (!kind) {
    return {
      ok: false,
      summary: 'Unsupported file type',
      message: 'Use an image (PNG, JPEG), video (MP4), or PDF.',
    };
  }
  if (file.size > ATTACHMENT_MAX_BYTES[kind]) {
    const maxMb = Math.round(ATTACHMENT_MAX_BYTES[kind] / (1024 * 1024));
    return {
      ok: false,
      summary: 'File too large',
      message: `Please select a ${kind} smaller than ${maxMb}MB.`,
    };
  }
  return { ok: true };
}
